/**
 * The tour content, one per route.
 *
 * These explain the *model*, not the buttons. Someone can see that a button
 * says "Add product"; what they cannot see is that stock in this system is
 * per-event rather than global, that a batch stops being editable the moment
 * it sells, or that closing an event is what returns unsold stock. Those are
 * the things a new cashier gets wrong, so those are what the steps say.
 */

import type { Tour } from './types';

const dashboard: Tour = {
  id: 'dashboard',
  title: 'Dashboard',
  steps: [
    {
      title: 'Selamat datang di Ophth POS',
      body: 'Tur singkat ini menjelaskan cara kerja sistem. Kamu bisa membukanya lagi kapan saja lewat tombol tanda tanya di kanan atas.',
    },
    {
      el: '[data-tour="active-event"]',
      title: 'Event aktif',
      body: 'Hampir semua hal di sistem ini terikat pada satu event. Stok, transaksi, dan laporan dihitung per event — jadi pastikan event di sini benar sebelum mulai berjualan.',
      side: 'bottom',
      align: 'start',
    },
    {
      el: '[data-tour="sidebar-nav"]',
      title: 'Menu utama',
      body: 'Alur normalnya: siapkan produk di Inventory, buat Event dan alokasikan stoknya, lalu jualan lewat POS. Transaksi, Piutang, dan Reports membaca hasilnya.',
      side: 'right',
      align: 'start',
    },
    {
      el: '[data-tour="page-header"]',
      title: 'Ringkasan hari ini',
      body: 'Angka di halaman ini mengikuti event yang sedang aktif, bukan seluruh riwayat.',
      side: 'bottom',
      align: 'start',
    },
  ],
};

const pos: Tour = {
  id: 'pos',
  title: 'POS (kasir)',
  steps: [
    {
      title: 'Layar kasir',
      body: 'Di sinilah penjualan dicatat. Layar ini hanya menampilkan produk yang stoknya dibawa ke event yang sedang berjalan.',
    },
    {
      el: '[data-tour="pos-products"]',
      title: 'Daftar produk',
      body: 'Yang tampil hanya produk yang dialokasikan ke event ini. Produk yang dibawa tapi sudah habis tetap muncul dengan tanda "Habis di event", supaya jelas bedanya dengan yang memang tidak dibawa.',
      side: 'right',
      keepIfMissing: true,
    },
    {
      el: '[data-tour="pos-customer"]',
      title: 'Pilih customer',
      body: 'Boleh dikosongkan untuk pembeli umum. Tapi invoice hanya bisa dikirim lewat WhatsApp kalau customernya dipilih, karena nomornya diambil dari sini. Piutang (tempo) juga wajib pakai customer.',
      side: 'bottom',
      align: 'start',
    },
    {
      el: '[data-tour="pos-cart"]',
      title: 'Keranjang',
      body: 'Kalau satu produk punya beberapa lot, sistem akan menanyakan lot mana yang diserahkan — tidak dipilih otomatis, karena nomor lot dan tanggal kedaluwarsa ikut tercetak di invoice customer.',
      side: 'left',
      keepIfMissing: true,
    },
    {
      el: '[data-tour="pos-fulfillment"]',
      title: 'Ambil sekarang atau kirim nanti',
      body: '"Ambil sekarang" langsung memotong stok. "Kirim nanti" menahan stok untuk pesanan itu tanpa memotongnya, sampai barang benar-benar diserahkan.',
      side: 'left',
      keepIfMissing: true,
    },
    {
      el: '[data-tour="pos-charge"]',
      title: 'Lanjut ke keranjang',
      body: 'Tombol ini membawa ke langkah dua: pilih lot (kalau produknya punya beberapa lot), nyalakan promo event, cek total, lalu bayar. Draft bisa disimpan dari sini juga.',
      side: 'top',
      keepIfMissing: true,
    },
  ],
};

const inventory: Tour = {
  id: 'inventory',
  title: 'Inventory',
  steps: [
    {
      title: 'Katalog produk',
      body: 'Halaman ini adalah daftar produk — bukan stok. Jumlah barang tidak disimpan di sini, melainkan per event. Itu sebabnya tidak ada kolom stok di halaman ini.',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Tambah, import, export',
      body: 'Import menerima file Excel/CSV dan mencocokkan lewat SKU: SKU yang sudah ada akan diperbarui, yang baru dibuat. Export menghasilkan file dengan kolom yang sama, jadi bisa diubah massal lalu dimasukkan kembali.',
      side: 'bottom',
      align: 'end',
    },
    {
      el: '[data-tour="inventory-filter"]',
      title: 'Aktif dan nonaktif',
      body: 'Produk nonaktif disembunyikan dari daftar dan dari POS, tapi SKU-nya tetap terpakai. Kalau menambah produk baru dan SKU-nya ditolak, ganti filter ke "Semua" — biasanya SKU itu dipegang produk yang sedang dinonaktifkan.',
      side: 'bottom',
      align: 'start',
    },
    {
      el: '[data-tour="inventory-table"]',
      title: 'Aksi per produk',
      body: 'Produk bisa dinonaktifkan (riwayat transaksinya aman) atau diaktifkan kembali. Hapus permanen hanya tersedia untuk produk yang belum pernah dipakai di event, transaksi, atau retur.',
      side: 'top',
      keepIfMissing: true,
    },
  ],
};

const events: Tour = {
  id: 'events',
  title: 'Events',
  steps: [
    {
      title: 'Event',
      body: 'Satu event mewakili satu kegiatan penjualan — seminar, pameran, atau kunjungan. Semua stok dan transaksi dikelompokkan per event.',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Buat event',
      body: 'Buat event dulu sebelum mengalokasikan stok. Nomor invoice dihitung per event, mulai dari 0001 setiap event baru.',
      side: 'bottom',
      align: 'end',
    },
    {
      el: '[data-tour="events-list"]',
      title: 'Kelola stok event',
      body: 'Ikon kotak pada setiap event membuka halaman alokasi stok — di situlah kamu menentukan barang apa dan berapa banyak yang dibawa.',
      side: 'top',
      keepIfMissing: true,
    },
    {
      title: 'Menutup event',
      body: 'Menutup event mengembalikan sisa stok ke gudang dan mengunci transaksi baru. Kalau ternyata masih ada yang perlu dicatat, event bisa dibuka kembali lewat menu titik tiga di kartu event.',
    },
  ],
};

const eventStock: Tour = {
  id: 'event-stock',
  title: 'Stok event',
  steps: [
    {
      title: 'Alokasi stok',
      body: 'Halaman ini menentukan barang apa saja yang dibawa ke event ini, per lot. Inilah sumber angka stok yang dilihat kasir di POS.',
    },
    {
      el: '[data-tour="stock-guide"]',
      title: 'Panduan singkat',
      body: 'Tiga jalur masuk (tambah manual, import Excel, template) dan satu jalur cek (export). Bisa dilipat; sistem mengingat pilihannya.',
      side: 'bottom',
      align: 'start',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Export dan kembali',
      body: 'Export stok mengunduh Excel per lot — qty masuk, terjual, sisa — untuk dicocokkan dengan hitungan fisik. Untuk daftar panjang, import Excel dengan template yang tersedia di dialog Import.',
      side: 'bottom',
      align: 'end',
    },
    {
      el: '[data-tour="stock-table"]',
      title: 'Satu baris = satu lot',
      body: 'Produk yang sama dengan nomor lot atau tanggal kedaluwarsa berbeda dicatat sebagai baris terpisah, supaya bisa ditelusuri kalau ada penarikan produk.',
      side: 'top',
      keepIfMissing: true,
    },
    {
      title: 'Aturan mengubah lot',
      body: 'Selama belum ada yang terjual, semua bisa diubah — qty, lot, tanggal kedaluwarsa, satuan, form order. Begitu satu unit terjual, datanya sudah tercetak di invoice customer, jadi hanya qty yang masih bisa disesuaikan.',
    },
  ],
};

const customers: Tour = {
  id: 'customers',
  title: 'Customers',
  steps: [
    {
      title: 'Data customer',
      body: 'Daftar pelanggan yang dipakai di POS, invoice, dan piutang.',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Tambah dan import',
      body: 'Import massal menerima kolom Nama, NIK/NPWP, Nomor Telepon, dan Alamat. Nomor yang sama tidak akan digandakan.',
      side: 'bottom',
      align: 'end',
    },
    {
      title: 'Format nomor telepon',
      body: 'Nomor disimpan dalam satu bentuk baku (+62…). Kamu boleh mengetik 0811… atau menempel langsung dari WhatsApp — sistem yang menyeragamkan. Nomor yang salah bentuk ditolak saat disimpan, karena invoice ke nomor keliru tidak menghasilkan pesan gagal apa pun.',
    },
  ],
};

const transactions: Tour = {
  id: 'transactions',
  title: 'Transactions',
  steps: [
    {
      title: 'Riwayat transaksi',
      body: 'Semua penjualan yang tercatat, dikelompokkan per event.',
    },
    {
      el: '[data-tour="search"]',
      title: 'Mencari transaksi',
      body: 'Cari lewat nomor invoice atau nama customer.',
      side: 'bottom',
      align: 'start',
    },
    {
      title: 'Membuka invoice',
      body: 'Klik satu baris untuk melihat invoice lengkapnya. Dari sana invoice bisa dicetak, diunduh sebagai PDF, atau dikirim ulang ke WhatsApp customer.',
    },
  ],
};

const returns: Tour = {
  id: 'returns',
  title: 'Returns',
  steps: [
    {
      title: 'Retur barang',
      body: 'Retur selalu mengacu pada satu invoice — tidak bisa retur lepas, supaya stok dan uang selalu punya asal-usul.',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Membuat retur',
      body: 'Cari invoicenya, pilih barang mana dan berapa banyak yang dikembalikan. Stok lot yang bersangkutan otomatis bertambah lagi.',
      side: 'bottom',
      align: 'end',
    },
  ],
};

const piutang: Tour = {
  id: 'piutang',
  title: 'Piutang',
  steps: [
    {
      title: 'Tagihan belum lunas',
      body: 'Transaksi dengan pembayaran tempo muncul di sini sampai lunas.',
    },
    {
      el: '[data-tour="page-header"]',
      title: 'Jatuh tempo',
      body: 'Tagihan yang lewat tanggal jatuh tempo ditandai terpisah, supaya tidak tenggelam di antara yang masih dalam tenggat.',
      side: 'bottom',
      align: 'start',
    },
    {
      title: 'Mencatat pembayaran',
      body: 'Pembayaran bisa dicicil. Setiap cicilan tercatat sendiri, dan sisa tagihannya ikut berkurang otomatis.',
    },
  ],
};

const reports: Tour = {
  id: 'reports',
  title: 'Reports',
  steps: [
    {
      title: 'Laporan',
      body: 'Ringkasan penjualan per event: total, produk terlaris, metode pembayaran, dan kinerja tiap kasir.',
    },
    {
      el: '[data-tour="page-actions"]',
      title: 'Ekspor',
      body: 'Laporan bisa diunduh sebagai Excel atau PDF untuk diserahkan ke bagian keuangan.',
      side: 'bottom',
      align: 'end',
    },
    {
      title: 'Laporan tidak tercampur',
      body: 'Setiap laporan hanya menghitung transaksi milik event yang dipilih. Dua event yang berjalan bersamaan tetap terpisah angkanya.',
    },
  ],
};

const settings: Tour = {
  id: 'settings',
  title: 'Settings',
  steps: [
    {
      title: 'Pengaturan',
      body: 'Identitas perusahaan, pajak, dan pengiriman invoice lewat WhatsApp diatur di sini.',
    },
    {
      title: 'Data perusahaan',
      body: 'Nama, alamat, logo, dan NPWP yang diisi di sini yang muncul di kop invoice — baik yang di layar maupun yang PDF.',
    },
    {
      title: 'WhatsApp',
      body: 'Ada dua cara mengirim invoice: sebagai link ke halaman invoice, atau sebagai file PDF yang langsung menempel di chat. Masing-masing butuh template sendiri yang sudah disetujui Meta, dan bisa ditukar kapan saja tanpa mengubah sistem.',
    },
  ],
};

const users: Tour = {
  id: 'users',
  title: 'Users',
  steps: [
    {
      title: 'Pengguna',
      body: 'Akun yang bisa masuk ke sistem. Owner bisa mengubah semua hal; cashier hanya bisa berjualan di POS.',
    },
    {
      el: '[data-tour="users-table"]',
      title: 'Mengelola akun',
      body: 'Password bisa diganti langsung oleh owner, atau dikirimkan sebagai link reset. Akun yang tidak dipakai sebaiknya dinonaktifkan, bukan dihapus — supaya riwayat transaksinya tetap punya nama.',
      side: 'top',
      keepIfMissing: true,
    },
  ],
};

const auditLogs: Tour = {
  id: 'audit-logs',
  title: 'Audit logs',
  steps: [
    {
      title: 'Jejak perubahan',
      body: 'Setiap perubahan penting dicatat di sini: siapa mengubah apa, kapan, dan alasannya. Catatan ini tidak bisa dihapus atau diubah.',
    },
    {
      el: '[data-tour="search"]',
      title: 'Menelusuri',
      body: 'Berguna saat angka stok terasa janggal — cari produk atau eventnya untuk melihat siapa yang terakhir menyesuaikannya dan mengapa.',
      side: 'bottom',
      align: 'start',
    },
  ],
};

/** Every tour, in menu order. */
const posCart: Tour = {
  id: 'pos-cart',
  title: 'Keranjang (langkah 2)',
  steps: [
    {
      title: 'Langkah dua',
      body: 'Di langkah satu produk hanya dihitung. Di sini ditentukan lot per unit, promo yang berlaku, dan totalnya — angka di sini sama persis dengan yang dihitung server saat bayar.',
    },
    {
      el: '[data-tour="cart-lines"]',
      title: 'Baris & lot',
      body: 'Produk dengan satu lot sudah otomatis. Produk dengan beberapa lot punya tabel: isi qty per lot sampai jumlahnya pas — lot dan expired date tercetak di invoice customer, jadi tidak ditebak sistem.',
      side: 'right',
      align: 'start',
    },
    {
      el: '[data-tour="cart-promos"]',
      title: 'Promo event',
      body: 'Centang promo yang berlaku: bundling, beli N gratis M, atau diskon bertingkat. Labelnya menunjukkan hemat berapa, atau kenapa belum berlaku. Urutan hitung: promo per baris → diskon bertingkat → diskon manual → PPN.',
      side: 'left',
      align: 'start',
    },
    {
      el: '[data-tour="cart-totals"]',
      title: 'Total & bayar',
      body: 'Pengiriman, diskon manual, PPN, lalu rincian total. Charge hanya aktif kalau semua lot sudah terisi dan promo yang dicentang benar-benar berlaku.',
      side: 'left',
      align: 'start',
    },
  ],
};

const eventDetail: Tour = {
  id: 'event-detail',
  title: 'Detail event',
  steps: [
    {
      el: '[data-tour="event-tabs"]',
      title: 'Empat bagian',
      body: 'Detail (fakta & edit), Stok (alokasi per lot, import/export), Akses (siapa saja yang boleh melihat event ini), Promo (bundling, beli-gratis, diskon bertingkat).',
      side: 'bottom',
      align: 'start',
    },
    {
      title: 'Akses',
      body: 'Kosong = semua staf sesuai rayon. Dicentang = hanya yang dicentang yang melihat event, transaksinya, dan stoknya. Pemegang hak events.edit tidak pernah terkunci.',
    },
    {
      title: 'Promo',
      body: 'Promo dibuat per event dan hanya untuk produk yang ada di stok event. Kasir menyalakannya di langkah keranjang; sistem yang menghitung dan menolak yang tidak berlaku.',
    },
  ],
};

export const ALL_TOURS: Tour[] = [
  posCart,
  eventDetail,
  dashboard, pos, inventory, events, eventStock, customers,
  transactions, returns, piutang, reports, settings, users, auditLogs,
];

/**
 * Which tour belongs to a path. Matched longest-prefix-first so
 * /events/:id/stock does not fall through to /events.
 */
export function tourForPath(pathname: string): Tour | null {
  const routes: [RegExp, Tour][] = [
    [/^\/events\/[^/]+\/stock/, eventStock],
    [/^\/events\/[^/]+/, eventDetail],
    [/^\/pos\/cart/, posCart],
    [/^\/dashboard/, dashboard],
    [/^\/pos/, pos],
    [/^\/inventory/, inventory],
    [/^\/customers/, customers],
    [/^\/events/, events],
    [/^\/returns/, returns],
    [/^\/transactions/, transactions],
    [/^\/piutang/, piutang],
    [/^\/reports/, reports],
    [/^\/settings/, settings],
    [/^\/users/, users],
    [/^\/audit-logs/, auditLogs],
  ];
  return routes.find(([re]) => re.test(pathname))?.[1] ?? null;
}
